import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useContacts } from '@/hooks/useApi'
import { useDebounce } from '@/hooks/useDebounce'
import { contactCompanyName } from '@/lib/company'
import type { Contact } from '@/types/api'

/** Valore dell'opzione che indica i dati di esempio invece di un contatto reale. */
const SAMPLE_VALUE = '__sample__'
/** Contatti mostrati per ricerca: oltre si affina il testo (#265). */
export const CONTACT_SEARCH_LIMIT = 20
export const CONTACT_SEARCH_DEBOUNCE_MS = 300

interface RenderContactSelectProps {
  /** Contatto scelto; null = dati di esempio. */
  value: Contact | null
  onChange: (contact: Contact | null) => void
}

function contactLabel(contact: Contact): string {
  const name = contact.full_name || contact.email
  const company = contactCompanyName(contact)
  return company ? `${name} — ${company}` : name
}

/**
 * Scelta del contatto con cui personalizzare anteprima e invio di test (#251):
 * lo stesso contatto produce lo stesso output dell'invio reale. La ricerca è
 * lato server (nome, email, azienda), così funziona anche con migliaia di
 * contatti (#265).
 */
export function RenderContactSelect({ value, onChange }: RenderContactSelectProps) {
  const { t } = useTranslation('sequences')
  const [search, setSearch] = useState('')
  const query = search.trim()
  const debouncedQuery = useDebounce(query, CONTACT_SEARCH_DEBOUNCE_MS)
  const { data, isFetching } = useContacts(
    1,
    CONTACT_SEARCH_LIMIT,
    debouncedQuery ? { search: debouncedQuery } : undefined,
  )
  // Finché il debounce non scade i risultati sono del testo precedente: non si
  // mostrano, così Invio non sceglie un contatto che non corrisponde a ciò che
  // è stato digitato
  const pending = query !== debouncedQuery
  const contacts = pending ? [] : (data?.items ?? [])
  const searching = isFetching || pending

  // I dati di esempio restano la prima scelta finché non si cerca un contatto
  const options = [
    ...(query ? [] : [{ value: SAMPLE_VALUE, label: t('preview.sampleData') }]),
    ...contacts.map((c) => ({ value: c.id, label: contactLabel(c) })),
  ]

  const handleChange = (optionValue: string) => {
    onChange(contacts.find((c) => c.id === optionValue) ?? null)
  }

  const hiddenCount = pending ? 0 : (data?.total ?? 0) - contacts.length

  return (
    <SearchableSelect
      value={value?.id ?? SAMPLE_VALUE}
      onValueChange={handleChange}
      options={options}
      onSearchChange={setSearch}
      selectedLabel={value ? contactLabel(value) : t('preview.sampleData')}
      searchPlaceholder={t('preview.searchContact')}
      emptyText={searching ? t('preview.searchingContacts') : t('preview.noContacts')}
      footer={
        hiddenCount > 0
          ? t('preview.moreContacts', { shown: contacts.length, total: data?.total })
          : undefined
      }
      className="h-8 text-xs"
      aria-label={t('preview.selectContact')}
    />
  )
}
